import React, { useState, useEffect, useRef } from 'react';
import { User, Phone, Mail, MapPin, Home, FileText } from 'lucide-react';
import { toast } from 'sonner';
import './BookingForm.css';

const BookingForm = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    squareFeet: '',
    serviceDescription: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.fullName || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading('Submitting your booking request...');
      
      // Submit to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (response.ok) {
        const result = await response.json();
        
        // Show success message
        toast.success('🎉 Thank you! Your booking request has been received. We will contact you shortly!');
        
        // Reset form
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          address: '',
          squareFeet: '',
          serviceDescription: ''
        });
      } else {
        const error = await response.json();
        toast.error(`Failed to submit booking: ${error.detail || 'Please try again'}`);
      }
    } catch (error) {
      toast.error('Network error. Please check your connection and try again.');
      console.error('Booking submission error:', error);
    }
  };

  return (
    <section id="booking" ref={sectionRef} className="booking-section">
      <div className="booking-container">
        <div className={`booking-content ${isVisible ? 'visible' : ''}`}>
          <div className="section-header">
            <span className="section-badge">Book Now</span>
            <h2 className="section-title">Get a Free Quote</h2>
            <p className="section-subtitle">
              Fill out the form below and we'll get back to you with a customized quote
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="booking-form">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  <User size={18} />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Phone size={18} />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="(555) 123-4567"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Mail size={18} />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <MapPin size={18} />
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="123 Main St, Roseville, CA"
                />
              </div>

              <div className="form-group full-width">
                <label className="form-label">
                  <Home size={18} />
                  Square Footage
                </label>
                <input
                  type="number"
                  name="squareFeet"
                  value={formData.squareFeet}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g., 1500"
                />
              </div>

              <div className="form-group full-width">
                <label className="form-label">
                  <FileText size={18} />
                  Service Description
                </label>
                <textarea
                  name="serviceDescription"
                  value={formData.serviceDescription}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Please describe the cleaning service you need..."
                  rows="5"
                ></textarea>
              </div>
            </div>

            <button type="submit" className="submit-button">
              Submit Request
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
